import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { SessionProvider } from './lib/session/SessionContext'
import { HomePage } from './pages/HomePage'
import { PricingPage } from './pages/PricingPage'
import { BrandPage } from './pages/BrandPage'
import { ProductPage } from './pages/ProductPage'
import { AgenciesPage } from './pages/AgenciesPage'
import { ResearchTeamsPage } from './pages/ResearchTeamsPage'
import { DemoLandingPage } from './pages/DemoLandingPage'
import { SecurityPage } from './pages/SecurityPage'
import { IndustryPage } from './pages/IndustryPage'
import { SignupPage } from './pages/SignupPage'
import { SigninPage } from './pages/SigninPage'
import { AuthVerifyPage } from './pages/AuthVerifyPage'
import { AppShell } from './app/AppShell'
import { DemoWorkspace } from './app/DemoWorkspace'
import { UploadPage } from './app/UploadPage'
import { ProjectOverview } from './app/ProjectOverview'
import { ProjectHomePage } from './app/ProjectHomePage'
import { AskComposerPage } from './app/AskComposerPage'
import { EchoPanelPage } from './app/EchoPanelPage'
import { PreReadPage } from './app/PreReadPage'
import { ValidationPage } from './app/ValidationPage'
import { ValidationOrchestrationPage } from './app/ValidationOrchestrationPage'
import { ReportPage } from './app/ReportPage'
import { DataPage } from './app/DataPage'
import { SettingsPage } from './app/SettingsPage'
import { BillingPage } from './app/BillingPage'
import { OnboardingPage } from './app/OnboardingPage'
import { PrincipalDashboard } from './app/PrincipalDashboard'
import { InboxPage } from './app/InboxPage'
import { ChurnValidationPage } from './app/ChurnValidationPage'
import { RenewalsPage } from './app/RenewalsPage'
import { CalibrationLedgerPage } from './app/CalibrationLedgerPage'
import { TemplatesPage } from './app/TemplatesPage'
import { PreReadDiffPage } from './app/PreReadDiffPage'
import { PipelineProcessingPage } from './app/PipelineProcessingPage'
import { PreReadComposerPage } from './app/PreReadComposerPage'
import { ExtensionAuthPage } from './app/ExtensionAuthPage'
import { IntegrationsPage } from './app/IntegrationsPage'
import { ReviewQueuePage } from './app/ReviewQueuePage'
import { LibraryPage } from './app/LibraryPage'
import { MethodologySettingsPage } from './app/MethodologySettingsPage'
import { HoursSavedPage } from './app/HoursSavedPage'
import { ProjectsListPage } from './app/ProjectsListPage'
import { BlogAdminPage } from './app/BlogAdminPage'
import { AdminFunnelPage } from './app/AdminFunnelPage'
import { MemoryStreamPage } from './app/MemoryStreamPage'
import { BroccoliScorePage } from './pages/BroccoliScorePage'
import { ResearchPage } from './pages/ResearchPage'
import { ResearchMagnetPage } from './pages/ResearchMagnetPage'
import { BlogPage, BlogPostPage } from './pages/BlogPage'
import { ResearchMethodologyPage } from './pages/ResearchMethodologyPage'
import { MethodologyPage } from './pages/MethodologyPage'
import { IntegrationsPublicPage } from './pages/IntegrationsPublicPage'
import { McpIntegrationsPage } from './pages/McpIntegrationsPage'
import { ApiKeysPage } from './app/ApiKeysPage'
import { TwinPanelOverviewPage } from './app/twin-panel/TwinPanelOverviewPage'
import { TwinsListPage } from './app/twin-panel/TwinsListPage'
import { TwinInspectorPage } from './app/twin-panel/TwinInspectorPage'
import { BulkQueryComposerPage } from './app/twin-panel/BulkQueryComposerPage'
import { BulkQueryResultPage } from './app/twin-panel/BulkQueryResultPage'
import { BulkQueriesListPage } from './app/twin-panel/BulkQueriesListPage'
import { TwinAccuracyPage } from './app/twin-panel/TwinAccuracyPage'
import { AccuracyMissesPage } from './app/twin-panel/AccuracyMissesPage'
import { RespondentQueryPage } from './app/twin-panel/RespondentQueryPage'
import { ProjectMeteringPage } from './app/ProjectMeteringPage'
import { ComingSoon } from './components/ComingSoon'

function AppIndexRedirect() {
  const session = localStorage.getItem('echo_session')
  return <Navigate to={session ? '/app/projects' : '/app/demo'} replace />
}

function PreReadDiffRoute() {
  const { projectId } = useParams<{ projectId: string }>()
  const search = new URLSearchParams(window.location.search)
  const baseId = search.get('base') ?? 'pr_demo_base'
  const targetId = search.get('against') ?? 'pr_demo_target'
  return <PreReadDiffPage projectId={projectId ?? 'demo'} basePreReadId={baseId} targetPreReadId={targetId} />
}

function ChurnValidationRoute() {
  const { projectId } = useParams<{ projectId: string }>()
  return <ChurnValidationPage projectId={projectId ?? 'demo'} demo={!projectId || projectId === 'demo'} />
}

function RenewalsRoute() {
  const { projectId } = useParams<{ projectId: string }>()
  return <RenewalsPage projectId={projectId} />
}

function ValidationOrchestrationRoute() {
  const { projectId } = useParams<{ projectId: string }>()
  return <ValidationOrchestrationPage projectId={projectId ?? 'demo'} preReadId="pr_demo" />
}

export function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          {/* Marketing site */}
          <Route path="/" element={<HomePage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/agencies" element={<AgenciesPage />} />
          <Route path="/research-teams" element={<ResearchTeamsPage />} />
          <Route path="/demo" element={<DemoLandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/brand" element={<BrandPage />} />
          <Route path="/industries/:industry" element={<IndustryPage />} />
          <Route path="/research/methodology/ablation" element={<ResearchMethodologyPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/integrations" element={<IntegrationsPublicPage />} />
          <Route path="/integrations/mcp" element={<McpIntegrationsPage />} />

          {/* Public tools + content */}
          <Route path="/tools/broccoli-score" element={<BroccoliScorePage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/research/:magnetId" element={<ResearchMagnetPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />

          {/* Auth */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signin" element={<SigninPage />} />
          <Route path="/auth/verify" element={<AuthVerifyPage />} />

          {/* App */}
          <Route path="/app" element={<AppShell />}>
            <Route index element={<AppIndexRedirect />} />
            <Route path="demo" element={<DemoWorkspace />} />
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="onboarding/processing" element={<PipelineProcessingPage />} />
            <Route path="upload" element={<UploadPage />} />
            <Route path="projects/:projectId/upload" element={<UploadPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="principal" element={<PrincipalDashboard />} />
            <Route path="calibration" element={<CalibrationLedgerPage />} />
            <Route path="projects" element={<ProjectsListPage />} />
            <Route path="projects/:projectId" element={<ProjectHomePage />} />
            <Route path="projects/:projectId/ask" element={<AskComposerPage />} />
            <Route path="projects/:projectId/echo-panel" element={<EchoPanelPage />} />
            <Route path="projects/:projectId/pre-read" element={<PreReadPage />} />
            <Route path="projects/:projectId/validation" element={<ValidationPage />} />
            <Route path="projects/:projectId/validation-orchestration" element={<ComingSoon featureName="Validation Orchestration" description="Automated validation scheduling against external panels is coming in a future release." availableOn="Agency Pro and above" />} />
            <Route path="projects/:projectId/report" element={<ReportPage />} />
            <Route path="projects/:projectId/data" element={<DataPage />} />
            <Route path="projects/:projectId/churn-validation" element={<ChurnValidationRoute />} />
            <Route path="projects/:projectId/renewals" element={<ComingSoon featureName="B2B Renewals" description="Renewal risk scoring and playbooks for B2B accounts are in active development." availableOn="Studio and above" />} />
            <Route path="projects/:projectId/pre-reads/diff" element={<PreReadDiffRoute />} />
            <Route path="projects/:projectId/pre-reads/new" element={<PreReadComposerPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="extension" element={<ExtensionAuthPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="review-queue" element={<ReviewQueuePage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="analytics/hours-saved" element={<ComingSoon featureName="Hours Saved Analytics" description="ROI and time-saved tracking across your research operations is in development." />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/methodology" element={<MethodologySettingsPage />} />
            <Route path="settings/api-keys" element={<ApiKeysPage />} />
            <Route path="projects/:projectId/twin-panel" element={<TwinPanelOverviewPage />} />
            <Route path="projects/:projectId/twin-panel/twins" element={<TwinsListPage />} />
            <Route path="projects/:projectId/twin-panel/twins/:twinId" element={<TwinInspectorPage />} />
            <Route path="projects/:projectId/twin-panel/queries" element={<BulkQueriesListPage />} />
            <Route path="projects/:projectId/twin-panel/queries/new" element={<BulkQueryComposerPage />} />
            <Route path="projects/:projectId/twin-panel/queries/:queryId" element={<BulkQueryResultPage />} />
            <Route path="projects/:projectId/respondents/query" element={<RespondentQueryPage />} />
            <Route path="projects/:projectId/twin-panel/accuracy" element={<TwinAccuracyPage />} />
            <Route path="projects/:projectId/twin-panel/accuracy/misses" element={<AccuracyMissesPage />} />
            <Route path="projects/:projectId/metering" element={<ProjectMeteringPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="admin/blog" element={<BlogAdminPage />} />
            <Route path="admin/funnel" element={<AdminFunnelPage />} />
            <Route path="projects/:projectId/memory" element={<MemoryStreamPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  )
}
